/** The preview table (2.J deliverable 11): the counts above, one row per planned row, 50 at a time. */
import type {
  BootstrapDecision,
  BootstrapPreviewRowV1,
  BootstrapPreviewV1,
} from '@codex/contracts';
import { Button } from '@codex/ui';
import { useState } from 'react';

export const PAGE = 50;

export const DECISION_WORDS: Readonly<Record<BootstrapDecision, string>> = {
  create: 'Add',
  untrash: 'Restore',
  skip: 'Skip',
  update: 'Update',
  record: 'Record',
  unchanged: 'No change',
};

export const keepsDateText = (number: number | undefined) =>
  `Session ${number ?? ''} keeps its earlier date.`.replace('  ', ' ');

function what(row: BootstrapPreviewRowV1): string {
  if (row.number !== undefined) return `Session ${row.number}`;
  return row.title ?? row.secretId ?? row.documentId ?? '';
}

function detail(row: BootstrapPreviewRowV1): string {
  if (row.dateKept === true) return keepsDateText(row.number);
  const parts = [
    row.number === undefined ? undefined : row.title,
    row.realDate,
    row.canonStatus,
    row.playState,
  ];
  return parts.filter((p): p is string => p !== undefined && p !== '').join(' · ');
}

export function countsLine(counts: BootstrapPreviewV1['counts']): string {
  return (Object.keys(DECISION_WORDS) as BootstrapDecision[])
    .filter((d) => counts[d] > 0)
    .map((d) => `${counts[d]} ${DECISION_WORDS[d].toLowerCase()}`)
    .join(', ');
}

export function PreviewTable({ preview }: { preview: BootstrapPreviewV1 }) {
  const [shown, setShown] = useState(PAGE);
  const rows = preview.rows.slice(0, shown);
  const left = preview.rows.length - rows.length;
  return (
    <section aria-label="Preview" className="cx-bootstrap__preview">
      <p role="status" className="cx-bootstrap__counts">
        {countsLine(preview.counts) || 'Nothing to change.'}
      </p>
      <table data-testid="bootstrap.preview.table" className="cx-bootstrap__table">
        <caption className="cx-visually-hidden">What applying this step writes</caption>
        <thead>
          <tr>
            <th scope="col">Change</th>
            <th scope="col">Row</th>
            <th scope="col">Detail</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: rows have no stable id of their own
            <tr key={i}>
              <td>{DECISION_WORDS[row.decision]}</td>
              <td>{what(row)}</td>
              <td>{detail(row)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {left > 0 && (
        <Button size="sm" onClick={() => setShown(shown + PAGE)}>
          {`Show more (${left} left)`}
        </Button>
      )}
    </section>
  );
}
